/* eslint-disable newline-before-return */
/* eslint-disable max-len */
/* eslint-disable no-console */
import { openDB } from 'idb';
import { PRESENCE_ENUMS } from '../libs/enums/presenceEnums';

let dbConnection = null;
let setupInProgress = false;

const setupDB = async () =>
{
	// Prevent concurrent calls to setupDB
	if (setupInProgress)
	{
		console.log('setupDB already in progress, waiting...');
		// Wait for existing setup to complete with proper async handling
		await new Promise((resolve) =>
		{
			const checkComplete = () =>
			{
				if (!setupInProgress)
				{
					resolve();
				}
				else
				{
					setTimeout(checkComplete, 100);
				}
			};

			checkComplete();
		});
		// Return existing connection or null if setup failed
		return dbConnection;
	}

	if (dbConnection !== null)
	{
		return dbConnection;
	}

	setupInProgress = true;
	console.log('Starting IndexedDB setup...');

	try
	{
		// Create a timeout promise to prevent hanging
		const timeoutPromise = new Promise((resolve, reject) =>
		{
			setTimeout(() =>
			{
				reject(new Error('IndexedDB setup timed out after 15 seconds'));
			}, 15000);
		});

		// Create the database opening promise
		const dbPromise = new Promise((resolve, reject) =>
		{
			const openDatabase = async () =>
			{
				try
				{
					console.log('Opening IndexedDB connection...');
					const dbInstance = await openDB('presence-database', 3, {
						upgrade(dbInstance, oldVersion, newVersion, transaction)
						{
							console.log(`IndexedDB upgrade: ${oldVersion} -> ${newVersion}`);
							try
							{
								if (oldVersion < 3)
								{
								// Create groups store
									if (!dbInstance.objectStoreNames.contains('groups'))
									{
										console.log('Creating groups object store...');

										const groupStore = dbInstance.createObjectStore('groups', { keyPath: 'id' });

										groupStore.createIndex('recentMessageTime-index', 'recentMessageTime');
									}
									else
									{
										const groupStore = transaction.objectStore('groups');

										if (!groupStore.indexNames.contains('recentMessageTime-index'))
										{
											console.log('Creating groups recentMessageTime index...');
											groupStore.createIndex('recentMessageTime-index', 'recentMessageTime');
										}
									}

									// Create broadcasts store
									if (!dbInstance.objectStoreNames.contains('broadcasts'))
									{
										console.log('Creating broadcasts object store...');

										const broadcastStore = dbInstance.createObjectStore('broadcasts', { keyPath: 'id' });

										broadcastStore.createIndex('recentMessageTime-index', 'recentMessageTime');
									}
									else
									{
										const broadcastStore = transaction.objectStore('broadcasts');

										if (!broadcastStore.indexNames.contains('recentMessageTime-index'))
										{
											console.log('Creating broadcasts recentMessageTime index...');
											broadcastStore.createIndex('recentMessageTime-index', 'recentMessageTime');
										}
									}

									// Create messages store
									if (!dbInstance.objectStoreNames.contains('messages'))
									{
										console.log('Creating messages object store...');

										const store = dbInstance.createObjectStore('messages', { keyPath: 'id' });

										store.createIndex('chatId-index', 'chatId');
										store.createIndex('chatId-attachmentType-index', [ 'chatId', 'attachmentType' ]);
										store.createIndex('chatId-createdAt-index', [ 'chatId', 'createdAt' ]);
									}
									else
									{
										const store = transaction.objectStore('messages');

										if (!store.indexNames.contains('chatId-index'))
										{
											console.log('Creating messages chatId index...');
											store.createIndex('chatId-index', 'chatId');
										}
										if (!store.indexNames.contains('chatId-attachmentType-index'))
										{
											console.log('Creating messages chatId-attachmentType index...');
											store.createIndex('chatId-attachmentType-index', [ 'chatId', 'attachmentType' ]);
										}
										if (!store.indexNames.contains('chatId-createdAt-index'))
										{
											console.log('Creating messages chatId-createdAt index...');
											store.createIndex('chatId-createdAt-index', [ 'chatId', 'createdAt' ]);
										}
									}

									// Create userData store
									if (!dbInstance.objectStoreNames.contains('userData'))
									{
										console.log('Creating userData object store...');
										dbInstance.createObjectStore('userData', { keyPath: 'id' });
									}

									// Create addOnsData store
									if (!dbInstance.objectStoreNames.contains('addOnsData'))
									{
										console.log('Creating addOnsData object store...');
										dbInstance.createObjectStore('addOnsData', { keyPath: 'id' });
									}

									// Create myMessage store
									if (!dbInstance.objectStoreNames.contains('myMessage'))
									{
										console.log('Creating myMessage object store...');

										const msgStore = dbInstance.createObjectStore('myMessage', { keyPath: 'id' });

										msgStore.createIndex('id-index', 'id');
									}
									else
									{
										const msgStore = transaction.objectStore('myMessage');

										if (!msgStore.indexNames.contains('id-index'))
										{
											console.log('Creating myMessage id index...');
											msgStore.createIndex('id-index', 'id');
										}
									}

									// Create localFileBlobs store (for tracking messages sent from this device)
									if (!dbInstance.objectStoreNames.contains('localFileBlobs'))
									{
										console.log('Creating localFileBlobs object store...');

										dbInstance.createObjectStore('localFileBlobs', { keyPath: 'messageId' });
									}
								}
								console.log('IndexedDB upgrade completed successfully');
							}
							catch (upgradeError)
							{
								console.error('Error during IndexedDB upgrade:', upgradeError);
								reject(upgradeError);
							}
						},
						blocked()
						{
							console.warn('IndexedDB upgrade blocked: Another connection is open. Please close other tabs/windows.');
							// Try to force close connections
							try
							{
								if (dbConnection)
								{
									dbConnection.close();
									dbConnection = null;
								}
							}
							catch (closeError)
							{
								console.warn('Error closing blocked connection:', closeError);
							}
						// Don't reject here - let the timeout handle it if needed
						},
						blocking()
						{
							console.warn('IndexedDB blocking upgrade: Current connection is preventing version upgrade.');
							try
							{
								if (dbConnection)
								{
									console.log('Closing current IndexedDB connection to allow upgrade...');
									dbConnection.close();
									dbConnection = null;
								}
							}
							catch (closeError)
							{
								console.warn('Error closing blocking connection:', closeError);
							}
						},
						terminated()
						{
							console.warn('IndexedDB connection terminated unexpectedly.');
							dbConnection = null;
						// Don't reject - this might be recoverable
						}
					});

					console.log('IndexedDB connection opened successfully');
					resolve(dbInstance);
				}
				catch (openError)
				{
					console.error('Failed to open IndexedDB:', openError);
					reject(openError);
				}
			};

			openDatabase();
		});

		// Race between the database opening and timeout
		dbConnection = await Promise.race([ dbPromise, timeoutPromise ]);

		console.log('IndexedDB setup completed successfully');
		return dbConnection;
	}
	catch (error)
	{
		console.error('Failed to setup IndexedDB:', error);
		// Reset connection and flags on error
		dbConnection = null;
		throw error;
	}
	finally
	{
		setupInProgress = false;
	}
};

export const checkDatabaseHealth = async () =>
{
	try
	{
		const db = await setupDB();

		// Check if database connection is valid
		if (!db || !db.objectStoreNames)
		{
			console.error('Database health check failed: Invalid database connection');

			return false;
		}

		// List of required object stores
		const requiredStores = [ 'groups', 'broadcasts', 'messages', 'userData', 'addOnsData', 'myMessage', 'localFileBlobs' ];

		// Check if all required stores exist
		for (const storeName of requiredStores)
		{
			if (!db.objectStoreNames.contains(storeName))
			{
				console.error(`Database health check failed: Missing object store '${storeName}'`);

				return false;
			}
		}

		// Try to perform a simple read operation on the messages store
		try
		{
			const tx = db.transaction('messages', 'readonly');
			const store = tx.objectStore('messages');

			// Just check if we can get the count - don't need the actual value
			await store.count();
			await tx.done;
		}
		catch (readError)
		{
			console.error('Database health check failed: Unable to read from messages store', readError);

			return false;
		}

		console.log('Database health check passed');

		return true;
	}
	catch (error)
	{
		console.error('Database health check failed with error:', error);

		return false;
	}
};

export const resetDatabaseConnection = async () =>
{
	try
	{
		// Close existing connection if it exists
		if (dbConnection)
		{
			console.log('Closing existing IndexedDB connection...');
			dbConnection.close();
			dbConnection = null;
		}

		// Small delay to ensure connection is fully closed
		await new Promise((resolve) => setTimeout(resolve, 100));

		// Reopen the database
		console.log('Reopening IndexedDB connection...');
		dbConnection = await setupDB();

		return dbConnection;
	}
	catch (error)
	{
		console.error('Error resetting database connection:', error);
		// Reset connection on error
		dbConnection = null;
		throw error;
	}
};

/**
 * Force close any existing IndexedDB connections and reset the connection state
 * Useful for resolving blocking upgrade issues
 */
export const forceCloseDatabaseConnections = async () =>
{
	try
	{
		console.log('Force closing IndexedDB connections...');

		// Close existing connection if it exists
		if (dbConnection)
		{
			dbConnection.close();
			dbConnection = null;
		}

		// Wait a bit for connections to close
		await new Promise((resolve) => setTimeout(resolve, 200));

		console.log('IndexedDB connections force closed successfully');
		return { success: true };
	}
	catch (error)
	{
		console.error('Error force closing database connections:', error);
		return { success: false, error: error.message };
	}
};

export const clearLocalDb = async () =>
{
	try
	{
		const db = await setupDB();
		const transactionStores = [ 'groups', 'messages', 'userData', 'myMessage', 'addOnsData', 'broadcasts', 'localFileBlobs' ];
		const existingStores = Array.from(db.objectStoreNames).filter((store) =>
			transactionStores.includes(store)
		);

		if (existingStores.length > 0)
		{
			const tx = db.transaction(existingStores, 'readwrite');

			await Promise.all(
				existingStores.map((store) => tx.objectStore(store).clear())
			);
			await tx.done;
		}

		console.log('Presence DB cleared successfully.');
		return { success: true, method: 'cleared' };
	}
	catch (error)
	{
		console.error('Error clearing individual stores:', error);
		return { success: false, error: error.message };
	}
};

export const deleteLocalDb = async () =>
{
	try
	{
		// Close existing connection first
		if (dbConnection)
		{
			dbConnection.close();
			dbConnection = null;
		}

		// Delete the database using Promise wrapper
		await new Promise((resolve, reject) =>
		{
			let resolved = false;
			const dbDeleteRequest = indexedDB.deleteDatabase('presence-database');

			const safeResolve = () =>
			{
				if (!resolved)
				{
					resolved = true;
					resolve();
				}
			};

			const safeReject = (error) =>
			{
				if (!resolved)
				{
					resolved = true;
					reject(error);
				}
			};

			dbDeleteRequest.onerror = (event) =>
			{
				console.error('Error deleting database:', event);
				safeReject(event);
			};

			dbDeleteRequest.onblocked = () =>
			{
				console.warn('The database deletion is blocked by an active connection. Please close all tabs.');
				safeReject(new Error('Database deletion blocked by active connection'));
			};

			dbDeleteRequest.onsuccess = () =>
			{
				console.log('Database deleted successfully');
				safeResolve();
			};

			// Add a timeout as a safety net (10 seconds)
			setTimeout(() =>
			{
				if (!resolved)
				{
					console.warn('Database deletion timed out');
					safeReject(new Error('Database deletion timed out'));
				}
			}, 10000);
		});

		// Ensure we always return an object
		console.log('deleteLocalDb completed successfully');
		return { success: true };
	}
	catch (error)
	{
		console.error('Failed to delete database:', error);
		try
		{
			console.log('Attempting to clear entire database as fallback...');
			const clearResult = await clearLocalDb();

			if (clearResult.success)
			{
				console.log('Presence DB cleared successfully as a fallback.');
				return { success: true, method: 'cleared' };
			}
			else
			{
				console.error('Failed to clear database as fallback:', clearResult.error);
				return { success: false, error: clearResult.error };
			}
		}
		catch (clearError)
		{
			console.error('Error clearing the presence database:', clearError);
			return { success: false, error: clearError.message };
		}
	}
};

export async function getAllGroups()
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('groups', 'readonly');
		const store = tx.objectStore('groups');
		const index = store.index('recentMessageTime-index');

		const results = [];
		let cursor = await index.openCursor(null, 'prev');

		while (cursor)
		{
			results.push(cursor.value);
			cursor = await cursor.continue();
		}

		await tx.done;

		return results;
	}
	catch (error)
	{
		console.error('Error retrieving groups:', error);
	}
}

export async function addGroupsToPresenceDb(groupsArray)
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('groups', 'readwrite');
		const store = tx.objectStore('groups');

		await store.clear();
		await Promise.all(groupsArray.map((data) => store.put(data)));
		await tx.done;
	}
	catch (error)
	{
		console.error('Error adding groups from the database:', error);
	}
}

export async function getBroadcastList()
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('broadcasts', 'readonly');
		const store = tx.objectStore('broadcasts');
		const index = store.index('recentMessageTime-index');

		const results = [];
		let cursor = await index.openCursor(null, 'prev');

		while (cursor)
		{
			results.push(cursor.value);
			cursor = await cursor.continue();
		}

		await tx.done;

		return results ?? [];
	}
	catch (error)
	{
		console.error('Error retrieving groups:', error);
	}
}

export async function addBroadcastToPresenceDb(broadcastArray)
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('broadcasts', 'readwrite');
		const store = tx.objectStore('broadcasts');

		await store.clear();
		await Promise.all(broadcastArray.map((data) => store.put(data)));
		await tx.done;
	}
	catch (error)
	{
		console.error('Error adding groups from the database:', error);
	}
}

export async function deleteGroupFromPresenceDb(groupId)
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('groups', 'readwrite');
		const store = tx.objectStore('groups');

		await store.delete(groupId);
		await tx.done;
	}
	catch (error)
	{
		console.error('Error deleting groups from the database:', error);
	}
}
export async function deleteChatFromBroadcastDb(groupId)
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('broadcasts', 'readwrite');
		const store = tx.objectStore('broadcasts');

		await store.delete(groupId);
		await tx.done;
	}
	catch (error)
	{
		console.error('Error deleting groups from the database:', error);
	}
}

export const addMessagesToLocalDb = async (messagesData) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('messages', 'readwrite');
		const store = tx.objectStore('messages');

		await Promise.all(messagesData?.map((msg) => store.put(msg)));
		await tx.done;
	}
	catch (e)
	{
		if (e.name === 'QuotaExceededError')
		{
			console.error('Storage is full! Unable to store more data.');
		}
		else
		{
			console.error('Error adding messages to the database:', e);
		}
	}
};

export const addEachMessageToLocalDb = async (messageData) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('messages', 'readwrite');
		const store = tx.objectStore('messages');

		// Add a single message to the store
		await store.put(messageData);

		await tx.done;
		console.log('Message added to local DB');
	}
	catch (e)
	{
		if (e.name === 'QuotaExceededError')
		{
			console.error('Storage is full! Unable to store more data.');
		}
		else
		{
			console.error('Error adding message to the database:', e);
		}
	}
};

export const deleteMessageToLocalDb = async (messageId) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('messages', 'readwrite');
		const store = tx.objectStore('messages');

		store.delete(messageId);

		await tx.done;
	}
	catch (e)
	{
		console.error('Error deleting message from the database:', e);
	}
};

export const addMyDataToLocalDb = async (myData) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('userData', 'readwrite');
		const store = tx.objectStore('userData');

		await store.put(myData);
		await tx.done;
	}
	catch (e)
	{
		console.error('Error adding myData to the database:', e);
	}
};

export const setAddonsDataToLocalDb = async (addonsData) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('addOnsData', 'readwrite');
		const store = tx.objectStore('addOnsData');

		await store.put(addonsData);
		await tx.done;
	}
	catch (e)
	{
		console.error('Error adding myData to the database:', e);
	}
};

export const getAddonsData = async (userId) =>
{
	try
	{
		// console.log('Fetching user data for ID:', userId);
		const db = await setupDB();
		const tx = db.transaction('addOnsData', 'readonly'); // Use 'readonly' since we're just reading
		const store = tx.objectStore('addOnsData');

		const userData = await store.get(userId); // Get user data based on userId

		await tx.done;

		return userData || null;
	}
	catch (error)
	{
		console.error('Error fetching user data from the database:', error);
	}
};

export const getUserDataByUerId = async (userId) =>
{
	try
	{
		// console.log('Fetching user data for ID:', userId);
		const db = await setupDB();
		const tx = db.transaction('userData', 'readonly'); // Use 'readonly' since we're just reading
		const store = tx.objectStore('userData');

		const userData = await store.get(userId); // Get user data based on userId

		await tx.done;

		return userData || null;
	}
	catch (error)
	{
		console.error('Error fetching user data from the database:', error);
	}
};

export const getAllFromIDB = async (collection) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction(collection, 'readonly');
		const store = tx.objectStore(collection);

		const allData = await store.getAll();

		await tx.done;

		return allData || null;
	}
	catch (error)
	{
		console.error('Error fetching user data from the database:', error);
		return null;
	}
};

export const getMessagesFromLocalDbPagination = async (chatId, page = 0) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('messages', 'readonly');
		const store = tx.objectStore('messages');

		const index = store.index('chatId-createdAt-index');
		const futureTime = new Date(new Date().valueOf() + (5 * 60 * 1000));

		const range = IDBKeyRange.bound(
			[ chatId, 0 ],
			[ chatId, futureTime ]
		);

		const allMessages = [];
		let cursor = await index.openCursor(range, 'prev');
		let count = 0;
		const pageSize = 50;
		const skip = page * pageSize;

		while (cursor && allMessages.length < pageSize)
		{
			if (count >= skip)
			{
				allMessages.push(cursor.value);
			}
			count++;
			cursor = await cursor.continue();
		}

		await tx.done;

		const reversedMessages = allMessages?.reverse();

		return {
			data    : reversedMessages,
			hasMore : count > page * pageSize
		};
	}
	catch (e)
	{
		console.error('Error retrieving messages from the database:', e);
		throw e;
	}
};

export const getBroadcastMessagesFromLocalDb = async (broadcastId) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('messages', 'readonly');
		const store = tx.objectStore('messages');

		// Get all messages and filter by broadcastId since we don't have a broadcastId index
		const allMessages = await store.getAll();
		const broadcastMessages = allMessages.filter((msg) => msg?.broadcastId === broadcastId);

		// Sort by createdAt ascending
		broadcastMessages.sort((a, b) => (a?.createdAt || 0) - (b?.createdAt || 0));

		await tx.done;

		return {
			data    : broadcastMessages,
			hasMore : false
		};
	}
	catch (e)
	{
		console.error('Error retrieving broadcast messages from the database:', e);
		return { data: [], hasMore: false };
	}
};

export const getAttachmentsfromLocalDb = async (chatId, attachmentType) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('messages', 'readonly');
		const store = tx.objectStore('messages');

		const index = store.index('chatId-attachmentType-index');
		const keyRange = IDBKeyRange.only([ chatId, attachmentType ]);
		const allMessages = await index.getAll(keyRange);

		await tx.done;

		return allMessages;
	}
	catch (e)
	{
		console.error('Error getting messages to the database:', e);
	}
};

export const deleteMessagesFromLocalDb = async (chatId, chatRoomInfo, userId) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('messages', 'readwrite');
		const store = tx.objectStore('messages');

		const index = store.index('chatId-index');
		const allMessages = await index.getAll(chatId);

		const callMsg = allMessages?.find((msg) =>
			msg?.callDetails?.sessionId === chatRoomInfo?.sessionId &&
        (msg?.callDetails?.status === PRESENCE_ENUMS.accepted || msg?.callDetails?.status === PRESENCE_ENUMS.connected) &&
         [ PRESENCE_ENUMS.left, PRESENCE_ENUMS.declined, PRESENCE_ENUMS.missed ].includes(msg?.callDetails?.participants[userId]?.status));

		const messgaes = allMessages?.filter((chat) => chat.id !== callMsg?.id);

		await Promise.all(messgaes.map((message) => store.delete(message?.id)));

		await tx.done;
	}
	catch (e)
	{
		console.error('Error adding messages to the database:', e);
	}
};

export const updateMessageById = async (messageId, key, newValue) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('messages', 'readwrite'); // Open a transaction with readwrite access
		const store = tx.objectStore('messages');

		// Get the existing message by ID
		const existingMessage = await store.get(messageId);

		if (existingMessage)
		{
			// Update the specific key with the new value
			existingMessage[key] = newValue;

			// Put the updated message back into the store
			await store.put(existingMessage);
			console.log('Message updated successfully:', existingMessage);
		}
		else
		{
			console.warn(`No message found for ID: ${messageId}`);
		}

		await tx.done; // Wait for the transaction to complete
	}
	catch (error)
	{
		console.error('Error updating message:', error);
	}
};

export async function searchMessagesByString(chatId, value)
{
	const db = await setupDB();
	const tx = db.transaction('messages', 'readonly');
	const store = tx.objectStore('messages');
	const index = store.index('chatId-createdAt-index');

	const range = IDBKeyRange.bound(
		[ chatId, 0 ],
		[ chatId, Date.now() ]
	);

	const results = [];
	let cursor = await index.openCursor(range, 'next');

	while (cursor)
	{
		const message = cursor.value;

		console.log(cursor.value, 'cursor.value');
		if (message.text && message?.messageType !== 'deletedMessage' && message.messageType !== 'information')
		{
			const text = message.text.toLowerCase();
			const searchTerm = value?.toString().toLowerCase();
			let matchPosition = text.indexOf(searchTerm);

			// Find all occurrences of the search term in this message
			while (matchPosition !== -1)
			{
				results.push({
					...message,
					matchIndex : matchPosition // Store the position of this match
				});
				matchPosition = text.indexOf(searchTerm, matchPosition + 1);
			}
		}
		cursor = await cursor.continue();
	}

	await tx.done;

	return results;
}

export const addMyMsgToDb = async (msg) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('myMessage', 'readwrite');
		const store = tx.objectStore('myMessage');

		// Check if the message with the same id already exists
		const existingMsg = await store.get(msg.id);

		if (existingMsg)
		{
			// If the message exists, update it
			existingMsg.message = msg.message;
			await store.put(existingMsg);
		}
		else
		{
			// If the message doesn't exist, add it
			await store.add(msg);
		}

		await tx.done;
	}
	catch (e)
	{
		console.error('Error adding or updating message in the database:', e);
	}
};

export const getMyMsgFromDb = async (chatId) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('myMessage', 'readonly');
		const store = tx.objectStore('myMessage');

		const allMessages = await store.getAll(chatId);

		await tx.done;

		return allMessages;
	}
	catch (e)
	{
		console.error('Error getting messages to the database:', e);
	}
};

export const clearAllMessagesFromDb = async () =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction('myMessage', 'readwrite');
		const store = tx.objectStore('myMessage');

		// Clear all entries in the store
		await store.clear();

		// Wait for the transaction to complete
		await tx.done;
		console.log('All messages cleared from the database.');
	}
	catch (e)
	{
		console.error('Error clearing messages from the database:', e);
	}
};

/**
 * Add a message ID to the localFileBlobs store to track that it was sent from this device
 * @param {string} messageId - The ID of the message that was sent
 * @returns {Promise<void>}
 */
export const addFileToLocalDb = async (messageId, fileBlob = null) =>
{
	try
	{
		if (!messageId)
		{
			console.warn('No message ID provided to addFileToLocalDb');
			return;
		}

		const db = await setupDB();
		const tx = db.transaction('localFileBlobs', 'readwrite');
		const store = tx.objectStore('localFileBlobs');

		// Store the message ID with a timestamp and optional file blob
		const dataToStore = {
			messageId,
			sentAt : new Date().valueOf()
		};

		// Only store blob if provided (for images/videos)
		if (fileBlob)
		{
			dataToStore.fileBlob = fileBlob;

			await store.put(dataToStore);
		}
		await tx.done;
	}
	catch (e)
	{
		console.error('Error adding sent message ID to the database:', e);
	}
};

/**
 * Check if a file is cached
 * @param {string} messageId - The ID of the message to check
 * @returns {Promise<boolean>} - Returns true if file is cached, false otherwise
 */
export const isFileCached = async (messageId) =>
{
	try
	{
		if (!messageId)
		{
			return false;
		}

		const db = await setupDB();
		const tx = db.transaction('localFileBlobs', 'readonly');
		const store = tx.objectStore('localFileBlobs');

		const sentMessage = await store.get(messageId);

		await tx.done;

		return Boolean(sentMessage);
	}
	catch (e)
	{
		console.error('Error checking if message was sent from this device:', e);
		return false;
	}
};

/**
 * Get the local file blob for a message sent from this device
 * @param {string} messageId - The ID of the message
 * @returns {Promise<Blob|null>} - Returns the blob if exists, null otherwise
 */
export const getLocalFileBlob = async (messageId) =>
{
	try
	{
		if (!messageId)
		{
			return null;
		}

		const db = await setupDB();
		const tx = db.transaction('localFileBlobs', 'readonly');
		const store = tx.objectStore('localFileBlobs');

		const sentMessage = await store.get(messageId);

		await tx.done;

		return sentMessage?.fileBlob || null;
	}
	catch (e)
	{
		console.error('Error getting local file blob from the database:', e);
		return null;
	}
};

export async function updateEntireGroupData(groupId, modifiedData)
{
	try
	{
		const db = await setupDB(); // Ensure the database is initialized
		const tx = db.transaction('groups', 'readwrite');
		const store = tx.objectStore('groups');

		// Get the existing group data
		console.log('Transaction state before get:', tx.mode);
		const existingGroupData = await store.get(groupId);

		console.log('Transaction state after get:', tx.mode);

		if (!existingGroupData)
		{
			console.error(`Group with ID ${groupId} not found.`);

			return;
		}

		// Merge the modified data into the existing group data
		const updatedGroupData = {
			...existingGroupData,
			...modifiedData
		};

		// Save the updated group data
		store.put(updatedGroupData);
		await tx.done;
	}
	catch (error)
	{
		console.error('Error updating group data in local DB:', error);
		throw error;
	}
}

export const getDocumentFromIdb = async (collection, id) =>
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction(collection, 'readonly'); // Use 'readonly' since we're just reading
		const store = tx.objectStore(collection);

		const document = await store.get(id); // Get user data based on id

		await tx.done;

		return document || null;
	}
	catch (error)
	{
		console.error('Error fetching user data from the database:', error);
	}
};

export async function addDocumentToIdb(collection, doc)
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction(collection, 'readwrite');
		const store = tx.objectStore(collection);

		// await store.clear();
		// await Promise.all(broadcastArray.map((data) => store.put(data)));
		store.put(doc);
		await tx.done;
	}
	catch (error)
	{
		console.error('Error adding groups from the database:', error);
	}
}

export async function updateDocumentInIdb(collectionName, id, value)
{
	try
	{
		const db = await setupDB();
		const tx = db.transaction(collectionName, 'readwrite');
		const store = tx.objectStore(collectionName);

		const existingDocument = await store.get(id);

		if (existingDocument)
		{
			const updatedDocument = { ...existingDocument, ...value }; // Merge the existing document with new values

			await store.put(updatedDocument); // Save the updated document
			// console.log('Document updated successfully:', updatedDocument);
		}
		else
		{
			console.warn(`No document found for ID: ${id}`);
		}
		await tx.done;
	}
	catch (error)
	{
		console.error('Error adding groups from the database:', error);
	}
}

/**
 * Checks if the logged-in user matches the user stored in IndexedDB
 * If they don't match, deletes and reinitializes the entire IndexedDB
 * @param {string} loggedInUserId - The ID of the currently logged-in user
 * @returns {Promise<{cleared: boolean, reason: string, method?: string}>} - Returns whether data was deleted and the reason
 */
export const checkAndClearIndexedDBIfUserMismatch = async (loggedInUserId) =>
{
	try
	{
		if (!loggedInUserId)
		{
			console.warn('No logged-in user ID provided');
			return { cleared: false, reason: 'No logged-in user ID provided' };
		}

		// Get all user data from IndexedDB (should be only one user)
		const allUserData = await getAllFromIDB('userData');

		// If no user data exists in IndexedDB, no need to delete
		if (!allUserData || allUserData.length === 0)
		{
			console.log('No user data found in IndexedDB - this might be first login');
			return { cleared: false, reason: 'No user data in IndexedDB' };
		}

		// Get the single stored user (there should be only one)
		const storedUser = allUserData[0];

		// If stored user ID doesn't match logged-in user ID, delete the database
		if (storedUser.id !== loggedInUserId)
		{
			console.warn(`User mismatch detected! Logged-in user: ${loggedInUserId}, Stored user: ${storedUser.id}`);
			console.log('Deleting IndexedDB due to user mismatch...');

			const deleteResult = await deleteLocalDb();

			if (deleteResult.success)
			{
				console.log(`IndexedDB ${deleteResult.method} successfully due to user mismatch`);
				return { cleared: true, reason: 'User mismatch detected', method: deleteResult.method };
			}
			else
			{
				console.error('Failed to delete IndexedDB:', deleteResult.error);
				return { cleared: false, reason: `Failed to delete: ${deleteResult.error}` };
			}
		}

		console.log(`User match confirmed: ${loggedInUserId}`);
		return { cleared: false, reason: 'User matches' };
	}
	catch (error)
	{
		console.error('Error checking user mismatch:', error);
		return { cleared: false, reason: `Error: ${error.message}` };
	}
};

/**
 * Check and request persistent storage to protect IndexedDB from browser eviction
 * First checks if storage is already persisted, then requests if needed
 * Helps Chrome/Firefox users - their IndexedDB data will be protected from eviction
 * Safari/iOS will return false, but auto-recovery from Firebase still works
 * @returns {Promise<{granted: boolean, supported: boolean, alreadyPersisted: boolean, details?: object}>}
 */
export const requestPersistentStorage = async () =>
{
	try
	{
		console.log('🔍 Checking persistent storage status...');

		// Check if Storage API is supported
		if (!navigator.storage || !navigator.storage.persist)
		{
			console.warn('⚠️ Persistent storage API not supported. Auto-recovery from Firebase is enabled.');
			return { granted: false, supported: false, alreadyPersisted: false };
		}

		// First, check if storage is already persisted
		const alreadyPersisted = await navigator.storage.persisted();

		console.log('Initial persistence check:', alreadyPersisted);

		if (alreadyPersisted)
		{
			// Already persisted, no need to request
			console.log('✅ Storage is already persistent. IndexedDB data protected from eviction.');
			return {
				granted          : true,
				supported        : true,
				alreadyPersisted : true
			};
		}

		// Not persisted yet, request persistent storage
		console.log('Requesting persistent storage...');
		const isPersisted = await navigator.storage.persist();

		console.log('Persist request result:', isPersisted);

		// Get storage estimate for additional context
		let estimate = null;

		if (navigator.storage.estimate)
		{
			estimate = await navigator.storage.estimate();
			console.log('Storage estimate:', estimate);
		}

		// Log appropriate message based on result
		if (isPersisted)
		{
			console.log('✅ Persistent storage granted. IndexedDB data now protected from eviction.');
		}
		else
		{
			console.warn('⚠️ Persistent storage not granted. Data may be evicted, but auto-recovery from Firebase is enabled.');
		}

		return {
			granted          : isPersisted,
			supported        : true,
			alreadyPersisted : false,
			details          : {
				persistResult : isPersisted,
				usage         : estimate?.usage,
				quota         : estimate?.quota
			}
		};
	}
	catch (error)
	{
		console.error('Error requesting persistent storage:', error);
		return { granted: false, supported: false, alreadyPersisted: false };
	}
};